export type CarePlanHistoryChange = {
  field?: string;
  questionrandomid?: string | null;
  previous?: unknown;
  current?: unknown;
};

export type CarePlanHistoryEntry = {
  _id?: string;
  editedAt?: string;
  dateTime?: string;
  editedBy?: { name?: string };
  authorName?: string;
  changes?: CarePlanHistoryChange[];
};

export type PlanHistoryRecord = {
  carePlanType?: string;
  editHistory?: CarePlanHistoryEntry[];
};

type NormalizedChangeSide = {
  answer: unknown;
  comment?: string;
  question?: string;
  type?: string;
  category?: string;
};

const FIELD_LABELS: Record<string, string> = {
  careTakerId: "Caretaker",
  icdCodes: "ICD Codes",
  conditions: "Conditions",
  deviceType: "Device Types",
  questions: "Question",
};

function isQuestionChangeValue(
  value: unknown,
): value is {
  answer?: unknown;
  comment?: string;
  question?: string;
  type?: string;
  category?: string;
} {
  return !!value && typeof value === "object" && "answer" in value;
}

export function normalizeChangeSide(value: unknown): NormalizedChangeSide {
  if (isQuestionChangeValue(value)) {
    return {
      answer: value.answer,
      comment: value.comment,
      question: value.question,
      type: value.type,
      category: value.category,
    };
  }
  return { answer: value };
}

function formatDeviceTypeLabel(value: string): string {
  return value
    .replace(/_/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

export function formatChangeAnswerForDisplay(
  field: string | undefined,
  answer: unknown,
): unknown {
  if (field === "deviceType" && Array.isArray(answer)) {
    return answer.map((item) =>
      typeof item === "string" ? formatDeviceTypeLabel(item) : String(item),
    );
  }
  return answer;
}

export function getChangeTitle(change: CarePlanHistoryChange): string {
  const curr = normalizeChangeSide(change.current);
  const prev = normalizeChangeSide(change.previous);

  if (change.field === "questions" || change.questionrandomid) {
    return curr.question || prev.question || "Question";
  }

  return FIELD_LABELS[change.field ?? ""] || change.field || "Field";
}

export function getChangeValueLabel(field: string | undefined): string {
  if (field === "questions" || field === "question") return "Answer";
  return "Value";
}

function getCareHistoryAnswerLines(answer: unknown): string[] | null {
  if (answer == null || answer === "") return null;
  if (Array.isArray(answer)) {
    return answer
      .filter((a) => a != null && String(a).trim() !== "")
      .map(String);
  }
  return [String(answer)];
}

function normalizedCareHistoryAnswer(answer: unknown): string {
  const lines = getCareHistoryAnswerLines(answer);
  if (!lines || lines.length === 0) return "";
  return lines.map((l) => String(l).trim()).join("\n");
}

function normalizedCareHistoryComment(comment: unknown): string {
  if (comment == null || comment === "") return "";
  return String(comment).trim();
}

export function hasMeaningfulCarePlanChange(c: CarePlanHistoryChange): boolean {
  const prev = normalizeChangeSide(c.previous);
  const curr = normalizeChangeSide(c.current);

  const prevA = normalizedCareHistoryAnswer(
    formatChangeAnswerForDisplay(c.field, prev.answer),
  );
  const currA = normalizedCareHistoryAnswer(
    formatChangeAnswerForDisplay(c.field, curr.answer),
  );
  const prevC = normalizedCareHistoryComment(prev.comment);
  const currC = normalizedCareHistoryComment(curr.comment);

  if (prevA !== currA || prevC !== currC) return true;

  if (c.field === "questions" || c.questionrandomid) {
    const prevQ = (prev.question ?? "").trim();
    const currQ = (curr.question ?? "").trim();
    return prevQ !== currQ;
  }

  return false;
}

export function getMeaningfulCarePlanChanges(
  changes: CarePlanHistoryChange[] | undefined,
): CarePlanHistoryChange[] {
  if (!Array.isArray(changes)) return [];
  return changes.filter(hasMeaningfulCarePlanChange);
}

export function CareHistoryAnswerDisplay({ answer }: { answer: unknown }) {
  const lines = getCareHistoryAnswerLines(answer);
  if (!lines || lines.length === 0) {
    return <span className="text-muted-foreground">—</span>;
  }
  if (Array.isArray(answer)) {
    return (
      <ol className="text-foreground mt-1 list-inside list-decimal space-y-1 pl-1 text-sm leading-relaxed marker:text-muted-foreground">
        {lines.map((line, li) => (
          <li key={li} className="pl-1">
            {line}
          </li>
        ))}
      </ol>
    );
  }
  return (
    <span className="text-foreground text-sm leading-relaxed">{lines[0]}</span>
  );
}
